/**
 * A call carried by the shell's own WebRTC instead of WebKit's — iOS only.
 *
 * Why this exists is one measured fact, five device passes wide: inside
 * WKWebView the microphone's RTP sender puts exactly zero bytes on the wire on
 * whichever side CallKit performed a call action for — the caller always, the
 * callee when the answer came through CallKit — with a track that reports
 * itself live and unmuted throughout. No lever a page has moves it: the same
 * track re-handed to its sender changed nothing, and a fresh getUserMedia was
 * muted by the system in the same breath. The cause is structural — WebKit
 * cannot honour CallKit's audio contract, which wants the audio unit started
 * from `provider didActivate` — so the peer connection, the capture and the
 * audio unit move into the shell, the way the кружочек recorder did, and the
 * page stays the brain: the state machine, the signalling, the restart policy,
 * the diagnostics and every pixel of the UI are still CallContext and
 * CallOverlay. `ios/App/App/CallPeerPlugin.swift` is the other end, kept in
 * step by hand.
 *
 * The signalling this class emits and accepts is byte-for-byte the wire format
 * PeerCall speaks — a native caller must interop with a Safari callee and the
 * other way round, and the server forwards without looking. Two kinds never
 * reach the plugin: `camera` is a fact for the UI on either side, and
 * `restart` is this side's restart *policy* asking for the native side's one
 * restart *mechanism*, an offer with fresh ICE credentials.
 *
 * The video the native connection receives is rendered by the shell in a
 * Metal view *under* a transparent WebView — see CallOverlay, which owns the
 * class that makes the page see-through, and setCallLayout below, which is how
 * the page says where the pictures go.
 */

import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import type { IceConfig } from '../api/client.ts';
import {
  describeConnection,
  describeMedia,
  gatheringNote,
  qualityNote,
  summariseConnection,
  summariseMedia,
  tallyCandidates,
  type CallNote,
  type StatEntry,
  iceErrorNotes,
  relatedAddressOf,
  type IceErrorRecord,
} from './callDiagnostics.ts';
import type { CallTuning } from '../api/client.ts';
import type { Facing } from './capture.ts';
import { describeOpus, withOpusOptions } from './opus.ts';
import {
  callTuning,
  MAX_RESTARTS,
  RELAY_GRACE_MS,
  type CallConnection,
  type PeerHandlers,
  type SignalPayload,
} from './webrtc.ts';

/** The plugin's name on both sides — `jsName` in CallPeerPlugin.swift. */
export const CALL_PEER_PLUGIN = 'CallPeer';

/**
 * Where the native pictures go, in CSS pixels of the page's own viewport.
 *
 * Every field is optional and an omitted field means «unchanged», so the two
 * writers — CallOverlay deciding which views exist, PipView reporting where
 * the small one is — can each say their half without clobbering the other's.
 * The remote view ignores `rect`: it is pinned to the controller's bounds on
 * the native side, so the keyboard-resize dance in ChatBridgeViewController
 * cannot move it.
 */
export interface CallLayout {
  remote?: boolean;
  /**
   * `'none'` rather than null for «put the self-view away», because on the
   * other side of the bridge a JSON null and an absent key arrive as the same
   * nil — and here they mean opposite things: hide the picture, or leave it
   * exactly as it is.
   */
  local?: 'pip' | 'full' | 'none';
  rect?: { x: number; y: number; width: number; height: number };
  mirror?: boolean;
  /**
   * The two pictures trade places: the far end goes into `rect` and the
   * self-view fills the screen. `local` still says whether the small square
   * exists at all and `remote` whether the far end is drawn — this says only
   * which of the two gets the rectangle, so a tap that swaps them moves no
   * other field.
   */
  swapped?: boolean;
}

export interface CallPeerCreateOptions {
  iceServers: RTCIceServer[];
  video: boolean;
  facing: Facing;
  width: number;
  height: number;
  fps: number;
  maxVideoBitrate: number;
  maxAudioBitrate: number;
  /** iceTransportPolicy 'relay' on the native side; see callRelayFirst. */
  relayOnly: boolean;
}

/**
 * What the native side announces, live only: the peer exists only while the
 * page that created it is alive, so unlike CallKit there is no cold-start
 * queue to drain — an event with nobody listening describes a call that has
 * already been torn down.
 */
export interface CallPeerEvents {
  /** An offer, answer or candidate the native connection produced, wire-shaped. */
  signal: { payload: SignalPayload };
  /** Already mapped to the DOM's vocabulary on the Swift side. */
  connectionState: { state: RTCPeerConnectionState };
  /** A remote track arrived or went away — not the far end's camera *choice*,
   * which still travels as the `camera` signal because muting is local. */
  remoteMedia: { audio?: boolean; video?: boolean };
  /** `address` is the local address the request left from — the interface the error is about. */
  iceError: { errorCode: number; errorText?: string; url?: string; address?: string };
  /** `gathered` is candidate types (`host`/`srflx`/`prflx`/`relay`), parsed
   * out of each candidate's SDP on the native side. `turnLog` is libwebrtc's
   * own account of the relay, which only the no-relay row reads. */
  gatheringComplete: { gathered: string[]; iceErrors: number; turnLog?: string[] };
  firstFrame: Record<string, never>;
  /** The native renderer reporting a remote frame's dimensions — proof the
   * far end's picture reached the view, whatever the screen then shows. */
  remoteVideoSize: { width: number; height: number };
}

/** The bridge, exported as a type so the tests can stand in a fake for it. */
export interface CallPeerPlugin {
  /**
   * Rejects with `code` 'denied' | 'busy' | 'failed'; see asSetupError.
   * Resolves with the shell's confession about itself: `videoCapture: true`
   * only once the capturer and the native views exist (absent counts as
   * false — a shell older than the field must not be told to shine through),
   * and the ICE servers it actually built, which the page audits below.
   */
  create: (
    options: CallPeerCreateOptions,
  ) => Promise<{ videoCapture?: boolean; iceServers?: number; iceUrls?: number } | undefined>;
  start: () => Promise<void>;
  handleSignal: (options: { payload: SignalPayload }) => Promise<void>;
  restartIce: () => Promise<void>;
  /** setConfiguration with the other ice policy — the fallback half of relay-first. */
  setIcePolicy: (options: { relayOnly: boolean }) => Promise<void>;
  setTrackEnabled: (options: { kind: 'audio' | 'video'; enabled: boolean }) => Promise<void>;
  /** Resolves on the new camera's first frame — the `firstFrame` rule from capture.ts. */
  flipCamera: (options: { facing: Facing }) => Promise<void>;
  setLayout: (options: CallLayout) => Promise<void>;
  /** Each RTCStatistics flattened to its values plus {id, type} — StatEntry's shape. */
  stats: () => Promise<{ entries: StatEntry[] }>;
  close: () => Promise<void>;
  addListener: <E extends keyof CallPeerEvents>(
    event: E,
    handler: (data: CallPeerEvents[E]) => void,
  ) => Promise<PluginListenerHandle>;
}

const CallPeer = registerPlugin<CallPeerPlugin>(CALL_PEER_PLUGIN);

interface CapacitorBridge {
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
}

/**
 * True only inside the iOS app, and only where the plugin is really
 * registered. The canonical skew guard, same as hasNativeCallKit and for the
 * same reason: this TS lands before the Swift does, and a bundle newer than
 * the shell it runs inside must take the web call path byte for byte rather
 * than call into nothing. False here is today's behaviour everywhere.
 */
export const hasNativeCallPeer: boolean = (() => {
  const capacitor = (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;
  if (capacitor?.getPlatform?.() !== 'ios') return false;
  return capacitor.isPluginAvailable?.(CALL_PEER_PLUGIN) === true;
})();

/**
 * Where the native pictures go. Fire and forget, like the кружочек preview's
 * setFrame and for the same reason: a layout that lands a frame late is a
 * picture that settles a frame late, never a call that fails.
 */
/**
 * One layout call in flight at a time, everything said meanwhile collapsed
 * into the next one.
 *
 * A dragged self-view reports a new rectangle every frame, and each report is
 * a round trip: JSON out, a hop onto the main thread, a promise back. Sent
 * unconditionally that outruns the trip — the calls queue, every one of them
 * carrying a rectangle the finger has already left, and the picture crawls
 * after the empty square the page draws through a backlog that only grows.
 * Measured on a device: seconds behind by the end of one drag, then catching
 * up in a rush once the finger came off. Which is why this is a collapse
 * rather than a plain «drop while busy» — the *newest* rectangle is the one
 * that matters, and the calls in between are worth nothing.
 *
 * They are merged rather than replaced because an omitted field means
 * «unchanged» and the two writers each say only their half (see CallLayout):
 * applying one merged payload is applying them in order.
 */
export function collapsingLayouts(
  send: (layout: CallLayout) => Promise<unknown>,
): (layout: CallLayout) => void {
  let inFlight = false;
  let pending: CallLayout | null = null;

  const push = (layout: CallLayout): void => {
    if (inFlight) {
      pending = { ...(pending ?? {}), ...layout };
      return;
    }
    inFlight = true;
    void send(layout)
      .catch(() => {})
      .then(() => {
        inFlight = false;
        const next = pending;
        pending = null;
        if (next) push(next);
      });
  };
  return push;
}

const pushCallLayout = collapsingLayouts((layout) => CallPeer.setLayout(layout));

export function setCallLayout(layout: CallLayout): void {
  if (!hasNativeCallPeer) return;
  pushCallLayout(layout);
}

/**
 * Ends a call the page has no memory of, at the moment the page starts.
 *
 * The connection outlives its page, and that is new: with WebKit's own
 * RTCPeerConnection a reloaded page took its call down with it, because the
 * call *was* page objects. The shell's peer is not — so a WebView reloaded
 * mid-call (iOS does that under memory pressure, and a video call in the
 * background is exactly when memory is short) left libwebrtc talking to the
 * far end with the camera lit, and nothing on screen to hang up with: the
 * only way out was killing the app. Called once when CallProvider mounts,
 * where a live call cannot exist yet, so closing is always the right answer.
 */
export function closeOrphanedNativeCall(): void {
  if (!hasNativeCallPeer) return;
  void CallPeer.close().catch(() => {});
}

/**
 * The plugin's refusal codes as the errors the page already knows how to read:
 * callMediaError switches on DOMException names, and `call/setup/*` slugs are
 * grouped by the same names — so a native «denied» must arrive wearing
 * NotAllowedError or the permission screen and the diagnostics both miss it.
 */
function asSetupError(failure: unknown): Error {
  const code = (failure as { code?: string } | null)?.code ?? '';
  const message = (failure as { message?: string } | null)?.message;
  const text = typeof message === 'string' && message ? message : `native peer ${code || 'failed'}`;
  if (code === 'denied') return new DOMException(text, 'NotAllowedError');
  if (code === 'busy') return new DOMException(text, 'NotReadableError');
  return failure instanceof Error ? failure : new Error(text);
}

/**
 * The shell's connection, wearing PeerCall's interface.
 *
 * Everything that is policy lives here and mirrors PeerCall line for line —
 * the restart budget and its asymmetry, the counters, the diagnostic slugs —
 * because the rows in `client_errors` are read side by side and must mean the
 * same thing whichever connection wrote them. Everything that is mechanism
 * (the RTCPeerConnection, the capture, the early-candidate queue, the audio
 * unit) lives in Swift.
 *
 * What is deliberately absent: the whole silent-microphone machinery
 * (armMicCheck, restartMicrophone). That machinery was the page fighting a
 * capture it could not own; the native audio unit is started from CallKit's
 * own `didActivate`, which is the fix rather than the workaround. Whether it
 * worked is still measured — CallContext reads audioBytesSent() at the same
 * moments and files the same `call/local/silent-sender/late` slug, so the
 * go/no-go metric survives the move.
 */
export class NativeCallPeer implements CallConnection {
  private readonly handlers: PeerHandlers;
  private readonly plugin: CallPeerPlugin;
  /** Whether this deployment has a relay at all; see gatheringComplete. */
  private readonly relayConfigured: boolean;
  private closed = false;
  /**
   * Whether the shell draws this call's video — `create`'s own answer, and
   * the only thing the overlay's transparency may be gated on. Without the
   * gate a video call went see-through over a shell that renders nothing
   * (веха B), and the chat list showed through a live call: the page must
   * never undress until the native layer has confirmed there is a picture
   * underneath.
   */
  private videoReady = false;
  /** Whether the renderer's first remote frame has been filed already. */
  private remoteSizeSeen = false;
  /** Set by `start()`, which only the caller calls — the restart path reads it. */
  private caller = false;
  /** The last state the native side announced; describe() reads it before awaiting. */
  private state: RTCPeerConnectionState = 'new';
  /** ICE errors as filed, not as fired — see fileIceErrors. */
  private iceErrors = 0;
  /**
   * Errors waiting for the end of gathering, which is when they can be judged:
   * the same rule as PeerCall's, because the native side sees the same thing a
   * desktop does, only worse — an iPhone carries a cellular interface and a
   * tunnel or three beside its Wi-Fi, and every one of them fails to resolve
   * the relay and fails to connect, five identical rows per url, while the
   * Wi-Fi allocates in silence. One call from such a phone was forty rows.
   */
  private pendingIceErrors: IceErrorRecord[] = [];
  /** The interfaces that produced a srflx candidate — the ones that reach anything. */
  private readonly reachedFrom = new Set<string>();
  private gatheringDone = false;
  /** What has been filed, so a url failing again from another interface is not a second finding. */
  private readonly filedIceErrors = new Set<string>();
  private rejectedCandidates = 0;
  private restarts = 0;
  private restartsExhausted = false;
  private listeners: Promise<PluginListenerHandle>[] = [];
  /** What this call is set to; the same answer `create` hands the shell. */
  private tuning: CallTuning;
  /** So the Opus line is one per call rather than one per restart. */
  private audioDescribed = false;
  /** The relay-first experiment's state, the same four steps as PeerCall's. */
  private relayPhase: 'off' | 'trying' | 'held' | 'fallen' = 'off';
  private relayTimer: ReturnType<typeof setTimeout> | null = null;

  private constructor(config: IceConfig, handlers: PeerHandlers, plugin: CallPeerPlugin) {
    this.handlers = handlers;
    this.plugin = plugin;
    this.relayConfigured = config.hasRelay;
    this.tuning = callTuning(config);
  }

  /**
   * A factory rather than a constructor because creation crosses the bridge:
   * the plugin opens the capture and builds the connection before this
   * resolves, and a refusal has to surface as the same error a refused
   * getUserMedia would. Listeners are attached first — the connection does not
   * exist yet, so nothing can fire early, and nothing can be missed.
   */
  static async create(
    config: IceConfig,
    options: { video: boolean; facing: Facing; caller?: boolean },
    handlers: PeerHandlers,
    plugin: CallPeerPlugin = CallPeer,
  ): Promise<NativeCallPeer> {
    const peer = new NativeCallPeer(config, handlers, plugin);
    // The caller alone, for the reason PeerCall gives: one side with only
    // relay candidates is enough to route the pair through coturn, and one
    // side is one fallback and no glare.
    const relayFirst = peer.tuning.callRelayFirst && options.caller === true;
    peer.relayPhase = relayFirst ? 'trying' : 'off';
    peer.listen();
    try {
      // The same numbers the page's own connection uses, from the same answer:
      // the two paths have to produce the same call, and a second copy of the
      // defaults here is exactly how they would come to disagree. The shell
      // ignores codec and degradation for now — see docs/NATIVE-CALLS-PLAN.md.
      const tuning = peer.tuning;
      const created = await plugin.create({
        iceServers: config.iceServers,
        video: options.video,
        facing: options.facing,
        width: tuning.callWidth,
        height: tuning.callHeight,
        fps: tuning.callFps,
        maxVideoBitrate: tuning.callVideoKbps * 1000,
        maxAudioBitrate: tuning.callAudioKbps * 1000,
        relayOnly: relayFirst,
      });
      peer.videoReady = created?.videoCapture === true;
      // The silent black screen, said out loud. A video call over a shell
      // that draws nothing keeps the opaque overlay and simply has no
      // picture — deliberate, but until this row it left no trace anywhere:
      // the shell logs to a console nobody reads and the page just doesn't
      // undress. The value tells the two causes apart: `false` is a shell
      // that could not hang its views, `undefined` one too old to have any.
      if (options.video && !peer.videoReady) {
        handlers.onDiagnostic({
          detail: 'call/native/no-video',
          message: `shell drew no video views · videoCapture=${String(created?.videoCapture)}`,
        });
      }
      peer.auditIceServers(config, created);
    } catch (failure) {
      peer.unlisten();
      throw asSetupError(failure);
    }
    return peer;
  }

  /**
   * Whether the ICE configuration survived the bridge.
   *
   * `call/gathering/no-relay` with no ICE error is the one failure whose
   * three causes look identical from the page — a dead relay, an unreachable
   * one, and one that never reached libwebrtc at all. The first two are
   * answerable from outside (a TURN allocation either works or does not); the
   * third is a bug on this side of the JSON with no witness. So the shell
   * reports what it built and this compares it against what was sent — and
   * says so before the call, rather than leaving a tally to be argued about
   * afterwards.
   */
  private auditIceServers(
    config: IceConfig,
    created: { iceServers?: number; iceUrls?: number } | undefined,
  ): void {
    const builtServers = created?.iceServers;
    const builtUrls = created?.iceUrls;
    // A shell older than these fields says nothing, which is not a finding.
    if (typeof builtServers !== 'number' || typeof builtUrls !== 'number') return;
    const sentServers = config.iceServers.length;
    const sentUrls = config.iceServers.reduce((total, server) => {
      const urls = server.urls;
      return total + (Array.isArray(urls) ? urls.length : 1);
    }, 0);
    if (builtServers === sentServers && builtUrls === sentUrls) return;
    this.handlers.onDiagnostic({
      detail: 'call/native/ice-lost',
      message: `shell built ${builtServers}/${sentServers} ice servers, ${builtUrls}/${sentUrls} urls`,
    });
  }

  /** Whether the overlay may go transparent over this call. See videoReady. */
  get rendersVideo(): boolean {
    return this.videoReady;
  }

  /** One guarded door for every event, so nothing lands on a closed peer. */
  private on<E extends keyof CallPeerEvents>(
    event: E,
    handler: (data: CallPeerEvents[E]) => void,
  ): void {
    this.listeners.push(
      this.plugin.addListener(event, (data) => {
        if (!this.closed) handler(data);
      }),
    );
  }

  private listen(): void {
    this.on('signal', ({ payload }) => {
      if (payload.kind === 'candidate') {
        const base = relatedAddressOf(payload.candidate.candidate ?? '');
        if (base) this.reachedFrom.add(base);
      }
      this.handlers.onSignal(payload);
    });

    this.on('connectionState', ({ state }) => {
      this.state = state;
      this.handlers.onConnectionChange(state);
      if (state === 'connected' && this.relayPhase === 'trying') this.holdRelay();
      if (state === 'failed') {
        // `failed` while trying is left to the grace timer, and this is the
        // side the rule was measured on — see PeerCall for the account of the
        // relay refusing a permission for a private peer address and
        // libwebrtc calling that a failure with the srflx candidate still in
        // flight. One short row, not call/failed: it is every relay-first call
        // from here, and each connected 200 ms later.
        if (this.relayPhase === 'trying') {
          this.handlers.onDiagnostic({
            detail: 'call/relay/early-failed',
            message: 'ice failed before the relayed pair · waiting out the grace',
          });
          return;
        }
        // Before the restart, not after — the same order and the same reason
        // as PeerCall: a restart discards the candidate pairs that failed, and
        // those are the whole of what there is to look at.
        void this.reportFailure();
        // After the relayed pair carried the call, a failure is real.
        if (this.relayPhase === 'held') {
          void this.allowDirect('ice failed through the relay');
          return;
        }
        this.restart();
      }
    });

    // A remote video track arriving or leaving. The far end's camera *button*
    // still travels as the `camera` signal — a disabled track goes on arriving
    // as a live track, which is the whole reason that signal exists.
    this.on('remoteMedia', ({ video }) => {
      if (video !== undefined) this.handlers.onRemoteCamera(video);
    });

    this.on('iceError', (facts) => {
      const record: IceErrorRecord = {
        errorCode: facts.errorCode,
        errorText: facts.errorText ?? '',
        url: facts.url ?? '',
        address: facts.address ?? null,
      };
      // Held until gathering ends, when the reached set is known. After that
      // — the forty-second timeouts of the interfaces that never went
      // anywhere — each is judged on arrival against the same set.
      if (!this.gatheringDone) {
        this.pendingIceErrors.push(record);
        return;
      }
      this.fileIceErrors([record]);
    });

    // The native side finished gathering: the moment «did the relay allocate?»
    // has an answer, whether or not the call goes on to work. The error count
    // is the event's own — counted where the errors happened, so a listener
    // attached late cannot undercount them.
    this.on('gatheringComplete', ({ gathered, turnLog }) => {
      this.gatheringDone = true;
      this.fileIceErrors(this.pendingIceErrors);
      this.pendingIceErrors = [];
      const tally = tallyCandidates(gathered);
      if (this.relayPhase === 'trying' && tally.relay === 0) {
        void this.allowDirect('relay gathered nothing');
      }
      // The count as filed, not the shell's raw one: the no-relay row should
      // name the errors this call's journal actually shows.
      const note = gatheringNote(tally, this.relayConfigured, this.iceErrors);
      if (!note) return;
      // The finding keeps its slug and its shape; what the library said about
      // the relay is appended to the same row, because a `relay 0` with no ICE
      // error is unreadable on its own — three different faults produce it,
      // and the account of which one is inside libwebrtc or nowhere.
      const account = turnLog?.length ? ` · ${turnLog.join(' · ')}` : '';
      this.handlers.onDiagnostic({ ...note, message: `${note.message}${account}` });
    });

    // Filed once, and filed when things are *right*: a black screen over
    // healthy inbound stats has two remaining explanations — frames not
    // reaching the renderer, or a renderer whose picture the compositing
    // hides — and only the native view itself can say which. This row present
    // means the frames arrived and the fault is in front of them; absent, the
    // track never fed the view. Sizes after the first are not news.
    this.on('remoteVideoSize', ({ width, height }) => {
      if (this.remoteSizeSeen) return;
      this.remoteSizeSeen = true;
      this.handlers.onDiagnostic({
        detail: 'call/native/remote-size',
        message: `remote picture reached the renderer · ${width}×${height}`,
      });
    });
  }

  private unlisten(): void {
    for (const pending of this.listeners) {
      void pending.then((handle) => handle.remove()).catch(() => {});
    }
    this.listeners = [];
  }

  /** The caller's opening move: the native side offers and signals back. */
  async start(): Promise<void> {
    this.caller = true;
    await this.plugin.start();
  }

  /** Everything that arrives over `call:signal`, in one door. */
  async accept(payload: SignalPayload): Promise<void> {
    if (this.closed) return;

    if (payload.kind === 'camera') {
      this.handlers.onRemoteCamera(payload.on);
      return;
    }

    if (payload.kind === 'restart') {
      // Asked for by the side that cannot offer. A callee that gets this has
      // misread its own role and must not answer it with an offer — the same
      // glare rule as PeerCall, because the far end may *be* a PeerCall.
      if (this.caller) await this.reoffer();
      return;
    }

    if (payload.kind === 'candidate') {
      try {
        await this.plugin.handleSignal({ payload });
      } catch {
        // One unusable candidate is normal — they are offered speculatively
        // and most of them lose. Counted rather than reported, into the line a
        // connection actually failing writes.
        this.rejectedCandidates += 1;
      }
      return;
    }

    // Offers and answers travel verbatim but for the audio parameters, which
    // have no API and live in the description itself — and which the shell
    // therefore cannot be told any other way. See lib/opus.ts, and PeerCall's
    // tuneAudio for why it is the *remote* description that configures this
    // side's encoder.
    await this.plugin.handleSignal({ payload: this.tuneAudio(payload) });
    // The answer is when there is something to connect to, so the answer is
    // when the wait for a relayed path starts — RELAY_GRACE_MS's own account.
    if (payload.kind === 'answer' && this.relayPhase === 'trying' && this.relayTimer === null) {
      this.relayTimer = setTimeout(
        () => void this.allowDirect(`nothing through the relay in ${RELAY_GRACE_MS / 1000}s`),
        RELAY_GRACE_MS,
      );
    }
  }

  /**
   * Files the ICE errors that are about the relay, once each — iceErrorNotes'
   * judgement, plus a memory across batches so an interface timing out
   * forty seconds later does not refile what gathering already said.
   */
  private fileIceErrors(records: readonly IceErrorRecord[]): void {
    for (const note of iceErrorNotes(records, this.reachedFrom)) {
      const key = `${note.detail}|${note.message}`;
      if (this.filedIceErrors.has(key)) continue;
      this.filedIceErrors.add(key);
      this.iceErrors += 1;
      this.handlers.onDiagnostic(note);
    }
  }

  /** The relayed pair carries the call: the experiment is on, said once. */
  private holdRelay(): void {
    this.relayPhase = 'held';
    this.clearRelayTimer();
    this.handlers.onDiagnostic({
      detail: 'call/relay/held',
      message: 'direct path forbidden · connected through the relay',
    });
  }

  /**
   * Lifts the ban on the direct path over the bridge and offers again. The
   * same move as PeerCall.allowDirect, and outside the restart count for the
   * same reason: a relay that did not answer is not a network that failed.
   */
  private async allowDirect(reason: string): Promise<void> {
    if (this.closed) return;
    if (this.relayPhase !== 'trying' && this.relayPhase !== 'held') return;
    this.relayPhase = 'fallen';
    this.clearRelayTimer();
    try {
      await this.plugin.setIcePolicy({ relayOnly: false });
    } catch (error) {
      this.handlers.onDiagnostic({
        detail: 'call/relay/fallback',
        message: `${reason} · setConfiguration refused · ${error instanceof Error ? error.message : 'unknown'}`,
      });
      return;
    }
    this.handlers.onDiagnostic({
      detail: 'call/relay/fallback',
      message: `${reason} · direct path allowed again`,
    });
    await this.reoffer();
  }

  private clearRelayTimer(): void {
    if (this.relayTimer === null) return;
    clearTimeout(this.relayTimer);
    this.relayTimer = null;
  }

  async describe(): Promise<string> {
    // Read before the await, all of it — the same rule as PeerCall.describe,
    // for the same reason: the connection carries on failing and closing while
    // stats() is in flight.
    const state = this.closed ? 'closed' : this.state;
    const counters = [
      `ice ${state}`,
      ...(this.iceErrors > 0 ? [`${this.iceErrors} ice server errors`] : []),
      ...(this.rejectedCandidates > 0 ? [`${this.rejectedCandidates} candidates rejected`] : []),
      ...(this.restarts > 0 ? [`${this.restarts} ice restarts`] : []),
    ];
    if (this.closed) return ['connection closed', ...counters].join(' · ');

    const entries = await this.stats();
    const media = describeMedia(summariseMedia(entries));
    return [
      describeConnection(summariseConnection(entries)),
      ...(media === null ? [] : [media]),
      ...counters,
    ].join(' · ');
  }

  async quality(): Promise<CallNote | null> {
    if (this.closed) return null;
    return qualityNote(summariseMedia(await this.stats()));
  }

  async audioBytesSent(): Promise<number | null> {
    if (this.closed) return null;
    return summariseMedia(await this.stats()).audioOut;
  }

  /** Everything the native getStats knows, flattened. Empty when it would not answer. */
  /** The far end's description, with what this side's Opus encoder needs. */
  private tuneAudio(payload: SignalPayload): SignalPayload {
    if (payload.kind !== 'offer' && payload.kind !== 'answer') return payload;
    if (payload.sdp.sdp === undefined) return payload;
    const tuned = withOpusOptions(payload.sdp.sdp, {
      fec: this.tuning.callAudioFec,
      maxAverageBitrate: this.tuning.callAudioKbps * 1000,
    });
    if (!this.audioDescribed) {
      this.audioDescribed = true;
      const line = describeOpus(tuned);
      if (line) this.handlers.onDiagnostic({ detail: 'call/audio/opus', message: line });
    }
    return { ...payload, sdp: { ...payload.sdp, sdp: tuned } };
  }

  async stats(): Promise<StatEntry[]> {
    try {
      return (await this.plugin.stats()).entries;
    } catch {
      // A connection that is going away can refuse this. Nothing to say then.
      return [];
    }
  }

  private async reportFailure(): Promise<void> {
    // The same order as PeerCall: whatever gathering has not judged yet is
    // judged now, so the failure row and the errors it explains land together.
    this.fileIceErrors(this.pendingIceErrors);
    this.pendingIceErrors = [];
    const description = await this.describe();
    this.handlers.onDiagnostic({
      detail: 'call/failed',
      message: `connection failed · ${description}`,
    });
  }

  setTrackEnabled(kind: 'audio' | 'video', enabled: boolean): void {
    if (this.closed) return;
    // Fire and forget: a mute that lands a beat late is a beat of sound, not a
    // failure worth surfacing over a control somebody is still holding.
    void this.plugin.setTrackEnabled({ kind, enabled }).catch(() => {});
    // The far side would just see a frozen frame otherwise — same sentence as
    // PeerCall, because the far side may be one.
    if (kind === 'video') this.handlers.onSignal({ kind: 'camera', on: enabled });
  }

  /**
   * Null on purpose: the picture is a native layer, and the page has no stream
   * to rebind. What the page does get is the promise settling on the new
   * camera's first frame, so the mirror can turn over with the picture rather
   * than half a second before it.
   */
  async flipCamera(facing: Facing): Promise<MediaStream | null> {
    await this.plugin.flipCamera({ facing });
    return null;
  }

  /**
   * The policy half of a restart; `restartIce` over the bridge is only the
   * mechanism. Counted and capped here so the two implementations agree about
   * when a network is gone, and asymmetric for the same glare reason: one side
   * offers, the other asks.
   */
  private restart(): void {
    if (this.closed) return;
    if (this.restarts >= MAX_RESTARTS) {
      if (!this.restartsExhausted) {
        this.restartsExhausted = true;
        this.handlers.onDiagnostic({
          detail: 'call/restart/exhausted',
          message: `${MAX_RESTARTS} ice restarts changed nothing`,
        });
      }
      return;
    }
    this.restarts += 1;
    if (!this.caller) {
      this.handlers.onSignal({ kind: 'restart' });
      return;
    }
    void this.reoffer();
  }

  /** The offer half of a restart, from whichever side asked for it. */
  private async reoffer(): Promise<void> {
    if (this.closed) return;
    try {
      await this.plugin.restartIce();
    } catch (error) {
      this.handlers.onDiagnostic({
        detail: 'call/restart',
        message: `ice restart refused · ${error instanceof Error ? error.name : 'unknown'}`,
      });
    }
  }

  get connectionState(): RTCPeerConnectionState {
    return this.state;
  }

  /** Ends the connection and the native capture with it. Safe twice. */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.clearRelayTimer();
    this.unlisten();
    // The native close is idempotent on its own queue, so the race between
    // this and a call that died on the Swift side first costs nothing.
    void this.plugin.close().catch(() => {});
  }
}
